import type {
  AdminDashboardCardsData,
  NumberOfRequestsData,
  OrderBalanceData,
  OrderCommitmentData,
  OrdersVolumeData,
  PaymentsVolumeData,
  StatisticsOverviewData,
} from '@/_helpers/interfaces/statistics/statistics-data.interface';
import axios from 'axios';
import { defineStore } from 'pinia';

export const useDashboardStore = defineStore('dashboard', () => {
  const getAverageReleaseDurationPerMonth = async (
    startDate: any,
    endDate: any,
    customerId?: number | null,
    projectId?: number | null
  ) => {
    try {
      const { data } = await axios.get(`/payments/average_release_duration_per_month_chart/`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          customer: customerId,
          project: projectId,
        },
      });

      return data;
    } catch (error: any) {
      console.error(error);

      throw error;
    }
  };

  const getCountsOnReports = async (customerId?: number | null, projectId?: number | null) => {
    try {
      const apisUrls = [
        '/customers/customers_count/',
        '/orders/purchase_orders/orders_count/',
        '/users/active_users_count/',
        'customers/customer_projects/projects_count/',
      ];

      const [
        {
          data: { count: customers },
        },
        {
          data: { count: orders },
        },
        {
          data: { count: users },
        },
        {
          data: { count: projects },
        },
      ] = await Promise.all(
        apisUrls.map(url => {
          return axios.get(url, {
            params: {
              customer: customerId,
              project: projectId,
            },
          });
        })
      );

      return { customers, orders, users, projects };
    } catch (error: any) {
      console.error(error);

      throw error;
    }
  };

  const getRolesDashboardStatistics = async (
    startDate: any,
    endDate: any,
    customerId?: number | null,
    projectId?: number | null
  ) => {
    try {
      const { data } = await axios.get(`/common/dashboard/`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          customer: customerId,
          project: projectId,
        },
      });

      return data;
    } catch (error: any) {
      console.error(error);

      throw error;
    }
  };

  const getStatisticsOverview = async (
    startDate: any,
    endDate: any,
    customerId?: number,
    includeRecurring: boolean = true
  ): Promise<StatisticsOverviewData> => {
    try {
      const { data } = await axios.get(`/common/statistics/overview/`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          customer: customerId,
          include_recurring: includeRecurring,
        },
      });

      return data;
    } catch (error: any) {
      console.error(error);

      throw error;
    }
  };

  const getNumberOfRequests = async (
    startDate: any,
    endDate: any,
    customerId?: number,
    includeRecurring: boolean = true
  ): Promise<NumberOfRequestsData> => {
    try {
      const { data } = await axios.get(`/common/statistics/number_of_requests/`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          customer: customerId,
          include_recurring: includeRecurring,
        },
      });
      return data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  };

  const getOrdersVolume = async (
    startDate: any,
    endDate: any,
    customerId?: number,
    includeRecurring: boolean = true
  ): Promise<OrdersVolumeData> => {
    try {
      const { data } = await axios.get(`/common/statistics/orders_volume/`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          customer: customerId,
          include_recurring: includeRecurring,
        },
      });
      return data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  };

  const getPaymentVolume = async (
    startDate: any,
    endDate: any,
    customerId?: number,
    includeRecurring: boolean = true
  ): Promise<PaymentsVolumeData> => {
    try {
      const { data } = await axios.get(`/common/statistics/payments_volume/`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          customer: customerId,
          include_recurring: includeRecurring,
        },
      });
      return data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  };

  const getOrderBalance = async (
    startDate: any,
    endDate: any,
    customerId?: number,
    includeRecurring: boolean = true
  ): Promise<OrderBalanceData> => {
    try {
      const { data } = await axios.get(`/common/statistics/order_balance/`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          customer: customerId,
          include_recurring: includeRecurring,
        },
      });
      return data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  };

  const getOrderCommitment = async (
    startDate: any,
    endDate: any,
    customerId?: number,
    includeRecurring: boolean = true
  ): Promise<OrderCommitmentData> => {
    try {
      const { data } = await axios.get(`/common/statistics/order_commitment/`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          customer: customerId,
          include_recurring: includeRecurring,
        },
      });
      return data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  };

  const getAdminDashboardCards = async (
    startDate: any,
    endDate: any,
    customerId?: number
  ): Promise<AdminDashboardCardsData> => {
    const { data } = await axios.get(`/common/dashboard/cards/`, {
      params: {
        start_date: startDate,
        end_date: endDate,
        customer: customerId,
      },
    });

    return data;
  };

  const exportStatistics = async (
    startDate: any,
    endDate: any,
    customerId?: number,
    exportFormat: 'csv' | 'xlsx' | 'pdf' = 'pdf',
    includeRecurring: boolean = true
  ) => {
    const { data } = await axios.get(`/common/statistics/export/`, {
      params: {
        start_date: startDate,
        end_date: endDate,
        customer: customerId,
        export_format: exportFormat,
        include_recurring: includeRecurring,
      },
    });
    return data;
  };

  return {
    getCountsOnReports,
    getAverageReleaseDurationPerMonth,
    getRolesDashboardStatistics,
    getStatisticsOverview,
    getNumberOfRequests,
    getOrdersVolume,
    getPaymentVolume,
    getOrderBalance,
    getOrderCommitment,
    getAdminDashboardCards,
    exportStatistics,
  };
});
